import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/AppContext';

const InvoiceReport = ({ invoice, onClose, isPOS = false, autoPrint = false }) => {
    const { t } = useTranslation();
    const { systemSettings } = useContext(AppContext);
    const printRef = useRef();
    const [viewMode, setViewMode] = useState(isPOS ? 'receipt' : 'a4');
    const [pendingPrint, setPendingPrint] = useState(null);
    const defaultReceiptWidth = parseInt(systemSettings?.receipt_width || '65', 10) || 65;
    const [receiptWidth, setReceiptWidth] = useState(defaultReceiptWidth);

    useEffect(() => {
        if (systemSettings?.receipt_width) {
            setReceiptWidth(parseInt(systemSettings.receipt_width, 10) || 65);
        }
    }, [systemSettings?.receipt_width]);

    const handlePrint = useCallback((mode) => {
        const printContent = printRef.current;
        if (!printContent) return;

        let printIframe = document.getElementById('receipt-print-iframe');
        if (!printIframe) {
            printIframe = document.createElement('iframe');
            printIframe.id = 'receipt-print-iframe';
            printIframe.style.position = 'absolute';
            printIframe.style.width = '0px';
            printIframe.style.height = '0px';
            printIframe.style.border = 'none';
            document.body.appendChild(printIframe);
        }

        const a4Style = `
            body { font-family: 'Inter', sans-serif; padding: 20px; color: #333; }
            .header { text-align: center; border-bottom: 2px solid #ddd; padding-bottom: 10px; margin-bottom: 20px; }
            .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #f9f9f9; }
            .totals { width: 50%; float: right; margin-top: 20px; }
            .totals-row { display: flex; justify-content: space-between; padding: 5px 0; }
            .totals-row.bold { font-weight: bold; border-top: 2px solid #333; padding-top: 10px; }
            .receipt-only { display: none; }
        `;

        const widthMm = receiptWidth || 65;
        const receiptStyle = `
            @page { 
                margin: 0; 
                size: auto; 
            }
            *, *:before, *:after { 
                box-sizing: border-box !important; 
            }
            html, body { 
                margin: 0; 
                padding: 0; 
                width: 100%;
                background: #fff;
            }
            body { 
                font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; 
                padding: 2mm 3.5mm 4mm 2mm; 
                color: #000; 
                width: ${widthMm}mm; 
                max-width: ${widthMm}mm; 
                margin: 0 auto; 
                line-height: 1.25; 
                font-weight: 700; 
                background: #fff; 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                overflow-x: hidden;
            }
            .header { text-align: center; margin-bottom: 8px; }
            .header h1 { margin: 0; font-size: 16px; font-weight: 900; letter-spacing: 0.5px; text-transform: uppercase; }
            .header h2 { margin: 2px 0 6px; font-size: 10.5px; font-weight: 900; letter-spacing: 0.8px; text-transform: uppercase; }
            .info-block { text-align: center; font-size: 9.5px; font-weight: 800; line-height: 1.35; }
            .info-row { display: flex; justify-content: space-between; margin-bottom: 2px; font-size: 9.5px; font-weight: 800; }
            table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 8px; table-layout: fixed; }
            th { background: #fff !important; color: #000 !important; border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 3px 1px; font-weight: 900; text-transform: uppercase; text-align: center; }
            td { padding: 3px 1px; vertical-align: middle; border-bottom: 1px solid #e5e7eb; font-weight: 800; text-align: center; }
            .totals-area { border-top: 2px solid #000; padding: 3px 0; margin-top: 3px; font-size: 9.5px; text-align: right; }
            .totals-row { display: flex; justify-content: flex-end; gap: 6px; padding: 2px 0; font-weight: 800; }
            .totals-row.grand-total { font-weight: 900; font-size: 12px; border-top: 2px solid #000; padding-top: 3px; margin-top: 3px; }
            .barcode-container { text-align: center; margin-top: 10px; padding-top: 8px; border-top: 1px solid #ddd; }
        `;

        const doc = printIframe.contentWindow.document;
        doc.open();
        doc.write(`
            <html>
                <head>
                    <title>Print Invoice ${invoice?.invoice_number || ''}</title>
                    <style>
                        ${mode === 'receipt' ? receiptStyle : a4Style}
                    </style>
                </head>
                <body>
                    ${printContent.innerHTML}
                </body>
            </html>
        `);
        doc.close();

        setTimeout(() => {
            printIframe.contentWindow.focus();
            printIframe.contentWindow.print();
        }, 250);
    }, [invoice, receiptWidth]);

    useEffect(() => {
        if (autoPrint && invoice) {
            setPendingPrint(isPOS ? 'receipt' : 'a4');
        }
    }, [autoPrint, invoice, isPOS]);

    useEffect(() => {
        if (pendingPrint) {
            const timer = setTimeout(() => {
                handlePrint(pendingPrint);
                setPendingPrint(null);
                if (autoPrint && onClose) onClose();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [pendingPrint, autoPrint, onClose, handlePrint]);

    const triggerPrint = (mode) => {
        setViewMode(mode);
        setPendingPrint(mode);
    };

    if (!invoice) return null;

    // Company Branding Values (From Settings or Defaults)
    const companyName = systemSettings?.company_name || 'SUGIMCO';
    const companySubtitle = systemSettings?.company_subtitle || 'SUPER MARCHE Yazbek';
    const companyLogo = systemSettings?.company_logo || '';
    const companyNif = systemSettings?.company_nif || 'M 243623 A 001';
    const companyRc = systemSettings?.company_rc || '2003 B 54';
    const companyPhone = systemSettings?.company_phone || '21812160';
    const companyPhone2 = systemSettings?.company_phone2 || '+23670500060';
    const companyAddress = systemSettings?.company_address || 'BANGUI (RCA)';
    const companyEmail = systemSettings?.company_email || 'supermarcheYazbek@yahoo.com';

    const fmtMoney = (val) => {
        const num = parseFloat(val || 0);
        if (isNaN(num)) return '0';
        return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
    };

    const displayTva = invoice.tva_rate !== undefined ? parseFloat(invoice.tva_rate) : 20;
    let grandTotal = parseFloat(invoice.total_ttc || invoice.total_amount || 0);
    const itemsSum = (invoice.items || []).reduce((sum, item) => sum + (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0)), 0);

    if (grandTotal <= 0 && itemsSum > 0) {
        grandTotal = itemsSum;
    }

    const subtotal = parseFloat(invoice.total_ht && parseFloat(invoice.total_ht) > 0 ? invoice.total_ht : (grandTotal / (1 + (displayTva / 100))));
    const taxAmount = parseFloat(invoice.total_tva && parseFloat(invoice.total_tva) > 0 ? invoice.total_tva : (grandTotal - subtotal));
    const amountPaid = invoice.amount_paid !== undefined && invoice.amount_paid !== null && invoice.amount_paid !== '' ? parseFloat(invoice.amount_paid) : parseFloat(grandTotal || 0);

    // Parse payment methods
    const pm = invoice ? (invoice.payment_methods || (invoice.type === 'CREDIT' ? 'Account' : invoice.payment_method) || 'Cash') : 'Cash';
    const pmLower = pm.toString().toLowerCase();
    const isWalkIn = !invoice.customer_name || invoice.customer_name === 'Walk-in Customer' || invoice.customer_name === t('Walk-in Customer');

    let customerDisplay = invoice.customer_name || t('Walk-in Customer');
    if (isWalkIn) {
        if (pmLower === 'card') customerDisplay = 'Card - Paid';
        else if (pmLower === 'credit' || pmLower === 'account') customerDisplay = 'Account - Unpaid';
        else customerDisplay = 'Walkin - Paid';
    } else {
        if (pmLower === 'card') customerDisplay = `${invoice.customer_name} (Card) - Paid`;
        else if (pmLower === 'credit' || pmLower === 'account') customerDisplay = `${invoice.customer_name} - Unpaid`;
        else customerDisplay = `${invoice.customer_name} - Paid`;
    }


    const widthMm = receiptWidth || 65;

    const reportContent = (
        <div
            ref={printRef}
            className={autoPrint ? '' : `bg-white shadow-2xl p-2 mx-auto transition-all border border-gray-200 ${viewMode === 'receipt' ? 'max-w-full font-sans text-xs text-black rounded-lg' : 'w-full max-w-3xl p-8 rounded-lg'}`}
            style={viewMode === 'receipt' ? { color: '#000', fontWeight: 800, width: `${widthMm}mm`, maxWidth: '100%', boxSizing: 'border-box' } : {}}
        >
            {viewMode === 'receipt' ? (
                <div style={{ color: '#000', fontFamily: "'Inter', sans-serif", padding: '1mm 2mm', width: '100%', boxSizing: 'border-box' }}>
                    {/* Header with Admin Configured Logo & Details */}
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                        {companyLogo ? (
                            <img src={companyLogo} alt="Logo" style={{ height: '36px', maxWidth: '140px', margin: '0 auto 4px', objectFit: 'contain' }} />
                        ) : (
                            <h1 style={{ fontSize: '16px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#000', margin: '0 0 3px 0', lineHeight: 1.1 }}>{companyName}</h1>
                        )}

                        <h2 style={{ fontSize: '10.5px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#000', margin: '2px 0 5px 0' }}>{companySubtitle}</h2>

                        <div style={{ fontSize: '9.5px', fontWeight: 800, color: '#000', lineHeight: 1.35 }}>
                            {companyNif && <p style={{ margin: '1px 0' }}>NIF : {companyNif}</p>}
                            {companyRc && <p style={{ margin: '1px 0' }}>RC : {companyRc}</p>}
                            {companyPhone && <p style={{ margin: '1px 0' }}>TEL : {companyPhone}</p>}
                            {companyAddress && <p style={{ margin: '1px 0' }}>{companyAddress}</p>}
                            {companyPhone2 && <p style={{ margin: '1px 0' }}>Tel: {companyPhone2}</p>}
                            {companyEmail && <p style={{ margin: '1px 0', textTransform: 'lowercase', wordBreak: 'break-all' }}>{companyEmail}</p>}
                        </div>
                    </div>

                    {/* Dotted Divider */}
                    <div style={{ borderTop: '2px dotted #000', margin: '10px 0' }}></div>

                    {/* Receipt Details */}
                    <div style={{ fontSize: '9px', fontWeight: 800, color: '#000', margin: '6px 0', paddingRight: '2mm', boxSizing: 'border-box', width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px', marginBottom: '2px' }}>
                            <span style={{ fontWeight: 900, flexShrink: 0, whiteSpace: 'nowrap' }}>Receipt #:</span>
                            <span style={{ fontWeight: 900, minWidth: 0, textAlign: 'right', wordBreak: 'break-all' }}>{invoice.invoice_number}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px', marginBottom: '2px' }}>
                            <span style={{ fontWeight: 900, flexShrink: 0, whiteSpace: 'nowrap' }}>Date:</span>
                            <span style={{ minWidth: 0, textAlign: 'right' }}>{new Date(invoice.created_at || Date.now()).toLocaleDateString()} {new Date(invoice.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px', marginBottom: '2px' }}>
                            <span style={{ fontWeight: 900, flexShrink: 0, whiteSpace: 'nowrap' }}>Customer:</span>
                            <span style={{ minWidth: 0, textAlign: 'right', wordBreak: 'break-word' }}>{customerDisplay}</span>
                        </div>
                    </div>

                    {/* Table with Dark Styled Header Banner */}
                    <div style={{ margin: '10px 0', borderRadius: '4px', overflow: 'visible', border: '1px solid #d1d5db', width: '100%', boxSizing: 'border-box' }}>
                        {(() => {
                            const globalDiscAmount = parseFloat(invoice.discount_total || invoice.discount || 0);
                            const itemsTtcSum = (invoice.items || []).reduce((sum, item) => sum + (parseFloat(item.unit_price || 0) * parseFloat(item.quantity || 1)), 0);
                            const globalDiscPercent = (globalDiscAmount > 0 && itemsTtcSum > 0) ? (globalDiscAmount / itemsTtcSum) * 100 : 0;
                            // CFA has no decimals: show whole numbers in the narrow receipt table
                            const fmtCell = (v) => String(Math.round(parseFloat(v || 0)));

                            return (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px', color: '#000', tableLayout: 'fixed', boxSizing: 'border-box' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#fff', color: '#000', fontWeight: 900, textTransform: 'uppercase', fontSize: '7.5px', borderTop: '1px solid #000', borderBottom: '1px solid #000' }}>
                                            <th style={{ padding: '3px 2px', textAlign: 'left', color: '#000', width: '25%' }}>Desc</th>
                                            <th style={{ padding: '3px 2px', textAlign: 'center', color: '#000', width: '8%' }}>Qty</th>
                                            <th style={{ padding: '3px 2px', textAlign: 'center', color: '#000', width: '9%' }}>Dis%</th>
                                            <th style={{ padding: '3px 2px', textAlign: 'right', color: '#000', width: '19%', whiteSpace: 'nowrap' }}>HT</th>
                                            <th style={{ padding: '3px 2px', textAlign: 'right', color: '#000', width: '19%', whiteSpace: 'nowrap' }}>TVA</th>
                                            <th style={{ padding: '3px 3px 3px 2px', textAlign: 'right', color: '#000', width: '20%', whiteSpace: 'nowrap' }}>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoice.items && invoice.items.map((item, idx) => {
                                            const qty = parseFloat(item.quantity || 1);
                                            const unitPrice = parseFloat(item.unit_price || 0);
                                            const discVal = parseFloat(item.discount || item.discount_percent || 0);
                                            const discType = item.discount_type || '%';

                                            let discPercent = 0;
                                            let totalDiscAmount = 0;
                                            const baseTotal = unitPrice * qty;

                                            if (discType === '%') {
                                                discPercent = discVal;
                                                totalDiscAmount = baseTotal * (discPercent / 100);
                                            } else {
                                                if (discVal > 0 && baseTotal > 0) {
                                                    if (discVal <= 100 && baseTotal >= 100 && discType !== 'CFA') {
                                                        discPercent = discVal;
                                                        totalDiscAmount = baseTotal * (discPercent / 100);
                                                    } else {
                                                        totalDiscAmount = discVal;
                                                        discPercent = (discVal / baseTotal) * 100;
                                                    }
                                                }
                                            }

                                            const totalTtc = Math.max(0, baseTotal - totalDiscAmount);
                                            const itemHt = totalTtc / (1 + (displayTva / 100));
                                            const totalHt = itemHt;
                                            const tvaAmt = totalTtc - totalHt;

                                            const displayDiscPercent = discPercent > 0 ? discPercent : globalDiscPercent;

                                            return (
                                                <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb', fontWeight: 800 }}>
                                                    <td style={{ padding: '3px 2px', textAlign: 'left', fontWeight: 900, wordBreak: 'break-word', fontSize: '8px' }}>{item.product_name || item.name}</td>
                                                    <td style={{ padding: '3px 2px', textAlign: 'center', fontWeight: 900, fontSize: '8px' }}>{qty}</td>
                                                    <td style={{ padding: '3px 2px', textAlign: 'center', fontWeight: 900, fontSize: '8px' }}>{displayDiscPercent > 0 ? `${fmtMoney(displayDiscPercent)}%` : '-'}</td>
                                                    <td style={{ padding: '3px 2px', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap', fontSize: '8px' }}>{fmtCell(totalHt)}</td>
                                                    <td style={{ padding: '3px 2px', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap', fontSize: '8px' }}>{fmtCell(tvaAmt)}</td>
                                                    <td style={{ padding: '3px 3px 3px 2px', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap', fontSize: '8px' }}>{fmtCell(totalTtc)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            );
                        })()}
                    </div>

                    {/* Totals Section */}
                    {(() => {
                        const totalInvDiscount = (invoice.items || []).reduce((acc, i) => {
                            const qty = parseFloat(i.quantity || 1);
                            const unitPrice = parseFloat(i.unit_price || 0);
                            const dVal = parseFloat(i.discount || i.discount_percent || 0);
                            const dType = i.discount_type || '%';
                            if (dType === '%') return acc + (unitPrice * qty * (dVal / 100));
                            return acc + dVal;
                        }, 0) + parseFloat(invoice.discount_total || invoice.discount || 0);

                        return (
                            <div style={{ width: '100%', fontSize: '9.5px', fontWeight: 800, color: '#000', margin: '10px 0 6px 0', boxSizing: 'border-box', paddingRight: '2mm' }}>
                                {totalInvDiscount > 0 && (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                            <span style={{ fontWeight: 900 }}>Total w/o Disc:</span>
                                            <span>CFA {fmtMoney(grandTotal + totalInvDiscount)}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#e11d48' }}>
                                            <span style={{ fontWeight: 900 }}>Discount:</span>
                                            <span>-CFA {fmtMoney(totalInvDiscount)}</span>
                                        </div>
                                    </>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                    <span style={{ fontWeight: 900 }}>Subtotal (HT):</span>
                                    <span>CFA {fmtMoney(subtotal)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                    <span style={{ fontWeight: 900 }}>TVA ({displayTva}%):</span>
                                    <span>CFA {fmtMoney(taxAmount)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', marginTop: '3px', borderTop: '2px solid #000', fontSize: '11.5px', fontWeight: 900 }}>
                                    <span style={{ textTransform: 'uppercase' }}>TOTAL (TTC):</span>
                                    <span style={{ fontSize: '12px' }}>CFA {fmtMoney(grandTotal)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                    <span style={{ fontWeight: 900 }}>Amount Paid:</span>
                                    <span>CFA {fmtMoney(amountPaid)}</span>
                                </div>
                                {grandTotal - amountPaid > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#000', fontWeight: 900 }}>
                                        <span>Balance Due:</span>
                                        <span>CFA {fmtMoney(grandTotal - amountPaid)}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                    <span style={{ fontWeight: 900 }}>Items Sold:</span>
                                    <span>{invoice.items ? invoice.items.reduce((s, i) => s + parseFloat(i.quantity || 0), 0) : 0}</span>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Barcode Footer */}
                    <div style={{ textAlign: 'center', paddingTop: '10px', marginTop: '10px', borderTop: '1px solid #d1d5db' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: '2px', height: '32px', margin: '3px 0' }}>
                            {[3, 1, 2, 4, 1, 3, 2, 1, 4, 2, 1, 3, 1, 4, 2, 3, 1, 2, 3, 1, 4, 1, 2, 3, 2, 1, 4, 1, 3, 2, 1, 4, 2, 1, 3, 2, 1, 4, 2, 1].map((w, i) => (
                                <div key={i} style={{ backgroundColor: '#000', height: '100%', width: `${w}px` }}></div>
                            ))}
                        </div>
                        <p style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 900, letterSpacing: '2px', margin: '3px 0 0 0', textAlign: 'center' }}>
                            {invoice.invoice_number ? String(invoice.invoice_number).replace('INV-', '') : '00076'}
                        </p>
                    </div>
                </div>
            ) : (
                /* A4 View Mode */
                <div>
                    <div className="header text-center border-b-2 border-gray-300 pb-3 mb-4">
                        {companyLogo && <img src={companyLogo} alt="Logo" className="h-16 mx-auto mb-2 object-contain" />}
                        <h1 className="text-2xl font-black text-gray-900 uppercase">{companyName}</h1>
                        <p className="text-sm font-semibold text-gray-600 uppercase">{companySubtitle}</p>
                        <p className="text-xs text-gray-500 mt-1">{companyNif} | {companyRc} | {companyPhone} | {companyAddress}</p>
                    </div>

                    <div className="flex justify-between mb-6">
                        <div className="space-y-1">
                            <div><span className="font-bold">{t('Bill To')}:</span> <span className="ml-2 font-medium">{customerDisplay}</span></div>
                            <div><span className="font-bold">{t('Invoice No')}:</span> <span className="ml-2 font-semibold">{invoice.invoice_number}</span></div>
                        </div>
                        <div className="text-right space-y-1">
                            <div><span className="font-bold">{t('Date')}:</span> <span>{new Date(invoice.created_at || Date.now()).toLocaleDateString()}</span></div>
                            <div><span className="font-bold">{t('Status')}:</span> <span className="uppercase font-bold">{invoice.status || 'PAID'}</span></div>
                            <div><span className="font-bold">{t('Payment Method')}:</span> <span>{pm}</span></div>
                        </div>
                    </div>

                    <div className="w-full overflow-x-auto max-w-full">
                        <table className="w-full mb-6 border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-50">
                                    <th className="py-2 text-left px-2">{t('Description')}</th>
                                    <th className="py-2 text-center px-2">{t('Qty')}</th>
                                    <th className="py-2 text-right px-2">{t('Price')}</th>
                                    <th className="py-2 text-right px-2">{t('Total')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {invoice.items && invoice.items.map((item, idx) => (
                                    <tr key={idx}>
                                        <td className="py-2 px-2 font-medium">{item.product_name || item.name}</td>
                                        <td className="py-2 text-center px-2">{parseFloat(item.quantity || 1).toFixed(0)}</td>
                                        <td className="py-2 text-right px-2">{parseFloat(item.unit_price || 0).toFixed(2)} CFA</td>
                                        <td className="py-2 text-right px-2 font-semibold">{((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)} CFA</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-end">
                        <div className="w-1/2 space-y-2 border-t pt-3">
                            {(() => {
                                const totalInvDiscount = (invoice.items || []).reduce((acc, i) => {
                                    const qty = parseFloat(i.quantity || 1);
                                    const unitPrice = parseFloat(i.unit_price || 0);
                                    const dVal = parseFloat(i.discount || i.discount_percent || 0);
                                    const dType = i.discount_type || '%';
                                    if (dType === '%') return acc + (unitPrice * qty * (dVal / 100));
                                    return acc + dVal;
                                }, 0) + parseFloat(invoice.discount_total || invoice.discount || 0);

                                return (
                                    <>
                                        {totalInvDiscount > 0 && (
                                            <>
                                                <div className="flex justify-between text-sm">
                                                    <span>{t('Total w/o Discount')}:</span>
                                                    <span className="font-medium">CFA {(grandTotal + totalInvDiscount).toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between text-sm text-red-600 font-bold">
                                                    <span>{t('Remise / Discount')}:</span>
                                                    <span>-CFA {totalInvDiscount.toFixed(2)}</span>
                                                </div>
                                            </>
                                        )}
                                        <div className="flex justify-between text-sm">
                                            <span>{t('Subtotal')} (HT):</span>
                                            <span className="font-medium">CFA {subtotal.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span>{t('Tax')} ({displayTva}%):</span>
                                            <span className="font-medium">CFA {taxAmount.toFixed(2)}</span>
                                        </div>
                                    </>
                                );
                            })()}
                            <div className="flex justify-between border-t pt-2 text-lg font-bold text-gray-900">
                                <span>{t('TOTAL TTC')}:</span>
                                <span>CFA {grandTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm text-gray-700">
                                <span>{t('Amount Paid')}:</span>
                                <span className="font-medium">CFA {amountPaid.toFixed(2)}</span>
                            </div>
                            {grandTotal - amountPaid > 0 && (
                                <div className="flex justify-between text-sm text-red-600 font-bold">
                                    <span>{t('Balance Due')}:</span>
                                    <span>CFA {(grandTotal - amountPaid).toFixed(2)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    if (autoPrint) {
        return <div style={{ display: 'none' }}>{reportContent}</div>;
    }

    return (
        <div className="bg-white text-gray-800 p-4 rounded-xl border shadow-2xl relative w-full flex flex-col">

            {/* View Mode & Width Toggle */}
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4 pb-3 border-b">
                <h2 className="font-bold text-lg">{t('Invoice Preview')}</h2>
                <div className="flex flex-wrap items-center gap-2">
                    {viewMode === 'receipt' && (
                        <div className="flex items-center gap-1.5 text-xs bg-gray-50 border border-gray-200 px-2 py-1 rounded-lg">
                            <span className="font-bold text-gray-500">{t('Width')}:</span>
                            {[58, 64, 65, 68, 72].map(w => (
                                <button
                                    key={w}
                                    type="button"
                                    onClick={() => setReceiptWidth(w)}
                                    className={`px-1.5 py-0.5 rounded text-xs font-bold transition ${receiptWidth === w ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`}
                                >
                                    {w}mm
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                        <button
                            className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${viewMode === 'receipt' ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => setViewMode('receipt')}
                        >
                            {t('Receipt')}
                        </button>
                        <button
                            className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${viewMode === 'a4' ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => setViewMode('a4')}
                        >
                            {t('A4 Size')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Print Area Preview */}
            <div className="flex-1 overflow-y-auto bg-gray-100 p-4 flex justify-center items-center max-h-[65vh] rounded-lg border border-gray-200">
                {reportContent}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
                <button onClick={onClose} className="px-5 py-2 border rounded-lg text-gray-600 hover:bg-gray-50 transition font-medium">{t('Close')}</button>
                <button onClick={() => triggerPrint('receipt')} className="px-5 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-bold transition shadow-sm">
                    {t('Print Receipt')}
                </button>
                <button onClick={() => triggerPrint('a4')} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-bold transition shadow-sm">
                    {t('Print A4')}
                </button>
            </div>
        </div>
    );
};

export default InvoiceReport;
